import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

const DEFAULTS = {
  tax_enabled: true,
  tax_rate: 0.0975,
  tax_mode: "exclusive",
  currency: "USD",
  receipt_footer: "Thank you for shopping at Maharlika Seafood Mart & Chow.",
};

export default function POSSettings() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/pos");
      setForm({ ...DEFAULTS, ...data });
    } catch {
      toast.error("Failed to load POS settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, tax_rate: Number(form.tax_rate) };
      await api.put("/settings/pos", payload);
      toast.success("POS settings saved");
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  if (loading) return <div className="text-[#FAF9F6]/60">Loading POS settings...</div>;

  return (
    <div data-testid="pos-settings-page" className="max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-3xl text-[#FAF9F6]">POS Settings</h1>
          <p className="text-sm text-[#FAF9F6]/50">Manage tax, currency, and receipt behavior.</p>
        </div>
        <button onClick={save} className="btn-chow w-full sm:w-auto" data-testid="pos-settings-save">
          <Save className="w-4 h-4 inline mr-1" /> Save Settings
        </button>
      </div>

      <div className="admin-card p-5 space-y-5">
        <div>
          <h2 className="font-heading text-xl text-[#D4AF37] mb-3">Tax Settings</h2>

          <label className="flex items-center gap-2 text-[#FAF9F6] text-sm mb-4">
            <input
              type="checkbox"
              checked={form.tax_enabled}
              onChange={(e) => setForm({ ...form, tax_enabled: e.target.checked })}
            />
            Enable sales tax
          </label>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#FAF9F6]/50 mb-1">Tax Rate</label>
              <input
                type="number"
                step="0.0001"
                value={form.tax_rate}
                onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
              />
              <div className="text-[10px] text-[#FAF9F6]/40 mt-1">Use 0.0975 for 9.75%, or 9.75 also works after save.</div>
            </div>

            <div>
              <label className="block text-xs text-[#FAF9F6]/50 mb-1">Tax Mode</label>
              <select
                value={form.tax_mode}
                onChange={(e) => setForm({ ...form, tax_mode: e.target.value })}
                className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
              >
                <option value="exclusive">Tax Exclusive — add tax on top</option>
                <option value="inclusive">Tax Inclusive — tax already included</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-heading text-xl text-[#D4AF37] mb-3">Receipt Settings</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-[#FAF9F6]/50 mb-1">Currency</label>
              <input
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
              />
            </div>
            <div>
              <label className="block text-xs text-[#FAF9F6]/50 mb-1">Receipt Footer</label>
              <input
                value={form.receipt_footer}
                onChange={(e) => setForm({ ...form, receipt_footer: e.target.value })}
                className="w-full bg-[#0B0E14] border border-[#D4AF37]/30 px-3 py-2 text-[#FAF9F6] rounded"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
